<template>
  <div class="hms-page">
    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-swap-horizontal me-2"></i>{{ $t('page.shift_handover', 'Shift Handover') }}</h4>
        </div>
        <button class="hms-btn hms-btn-primary" @click="openNew"><i class="mdi mdi-plus me-1"></i>New handover</button>
      </div>

      <div class="hms-tabs mb-3">
        <button class="hms-tab" :class="{ active: tab === 'open' }" @click="tab='open'; load()">Open</button>
        <button class="hms-tab" :class="{ active: tab === 'all' }" @click="tab='all'; load()">All</button>
        <button class="hms-tab" :class="{ active: tab === 'approved' }" @click="tab='approved'; load()">Approved</button>
        <button class="hms-tab" :class="{ active: tab === 'overdue' }" @click="tab='overdue'; load()">Overdue</button>
      </div>

      <div v-if="!handovers.length" class="hms-muted text-center py-5">No handovers in this view.</div>

      <table v-else class="hms-table">
        <thead>
          <tr><th>Date</th><th>Shift</th><th>Outgoing</th><th>Incoming</th><th>Unit / Dept</th><th>TAT</th><th>Status</th><th></th></tr>
        </thead>
        <tbody>
          <tr v-for="h in handovers" :key="h.handover_id">
            <td>{{ h.shift_date }}</td>
            <td>{{ h.shift_type }}</td>
            <td>{{ (h.out_first || '') + ' ' + (h.out_last || '') }}</td>
            <td>{{ (h.in_first || '') + ' ' + (h.in_last || '') || '—' }}</td>
            <td>{{ h.unit_name || h.department || '—' }}</td>
            <td>{{ h.tat_minutes ? h.tat_minutes + ' min' : '—' }}</td>
            <td><span class="hms-badge" :class="statusBadge(h.status)">{{ h.status }}</span></td>
            <td>
              <button class="hms-btn-icon" @click="openView(h)" title="View"><i class="mdi mdi-eye"></i></button>
              <button v-if="h.status === 'submitted'" class="hms-btn-icon hms-text-green" @click="approve(h)" title="Approve"><i class="mdi mdi-check"></i></button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- New handover modal -->
    <div v-if="showForm" class="hms-modal-bg" @click.self="showForm=false">
      <div class="hms-modal" style="max-width:760px;">
        <div class="hms-modal-head">
          <h5>New shift handover</h5>
          <button class="hms-icon-btn" @click="showForm=false"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="row g-3">
            <div class="col-md-4"><label class="hms-label">Shift type</label>
              <select v-model="form.shift_type" class="hms-input">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
                <option value="night">Night</option>
                <option value="on_call">On-call</option>
              </select>
            </div>
            <div class="col-md-4"><label class="hms-label">Shift date</label>
              <input type="date" v-model="form.shift_date" class="hms-input" />
            </div>
            <div class="col-md-4"><label class="hms-label">Incoming staff ID</label>
              <input type="number" v-model.number="form.incoming_staff_id" class="hms-input" placeholder="(optional)" />
            </div>
            <div class="col-md-6"><label class="hms-label">Unit</label>
              <select v-model.number="form.unit_id" class="hms-input">
                <option :value="null">— Unit —</option>
                <option v-for="u in units" :key="u.unit_id" :value="u.unit_id">{{ u.unit_name }}</option>
              </select>
            </div>
            <div class="col-md-6"><label class="hms-label">Department (free text)</label>
              <input v-model="form.department" class="hms-input" />
            </div>

            <div class="col-12"><label class="hms-label">Critical patients</label>
              <textarea v-model="form.critical_patients" rows="2" class="hms-input" placeholder="Bed 4 — Mr. Mwangi (post-op day 1, watch BP); Bed 11 — Mary (PPH risk)…"></textarea>
            </div>
            <div class="col-12"><label class="hms-label">Pending tasks</label>
              <textarea v-model="form.pending_tasks" rows="2" class="hms-input" placeholder="Drug round at 22:00; MO review pending for bed 7; family wants to see bed 9…"></textarea>
            </div>
            <div class="col-12"><label class="hms-label">Equipment status</label>
              <textarea v-model="form.equipment_status" rows="2" class="hms-input" placeholder="Suction in bay 2 not working; oxygen cylinder 30% in bed 5…"></textarea>
            </div>
            <div class="col-12"><label class="hms-label">Meds to check</label>
              <textarea v-model="form.meds_to_check" rows="2" class="hms-input" placeholder="Insulin in bed 11 — recheck BG at 23:00…"></textarea>
            </div>
            <div class="col-12"><label class="hms-label">General notes</label>
              <textarea v-model="form.general_notes" rows="2" class="hms-input"></textarea>
            </div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-neutral" @click="showForm=false">Cancel</button>
          <button class="hms-btn hms-btn-primary" @click="save" :disabled="saving">
            <i class="mdi mdi-content-save me-1"></i>{{ saving ? 'Submitting…' : 'Submit handover' }}
          </button>
        </div>
      </div>
    </div>

    <!-- View modal -->
    <div v-if="viewing" class="hms-modal-bg" @click.self="viewing=null">
      <div class="hms-modal" style="max-width:760px;">
        <div class="hms-modal-head">
          <h5>Handover #{{ viewing.handover_id }}</h5>
          <button class="hms-icon-btn" @click="viewing=null"><i class="mdi mdi-close"></i></button>
        </div>
        <div class="hms-modal-body">
          <div class="hms-kv"><span class="hms-kv-key">Shift</span><span>{{ viewing.shift_type }} · {{ viewing.shift_date }}</span></div>
          <div class="hms-kv"><span class="hms-kv-key">Outgoing</span><span>{{ viewing.out_first }} {{ viewing.out_last }}</span></div>
          <div class="hms-kv"><span class="hms-kv-key">Incoming</span><span>{{ viewing.in_first }} {{ viewing.in_last || '—' }}</span></div>
          <div class="hms-kv"><span class="hms-kv-key">Unit / Dept</span><span>{{ viewing.unit_name || viewing.department || '—' }}</span></div>
          <div class="hms-kv"><span class="hms-kv-key">TAT</span><span>{{ viewing.tat_minutes ? viewing.tat_minutes + ' min' : '—' }}</span></div>
          <hr>
          <div v-if="viewing.critical_patients"><strong>Critical patients</strong><div class="hms-pre">{{ viewing.critical_patients }}</div></div>
          <div v-if="viewing.pending_tasks"><strong>Pending tasks</strong><div class="hms-pre">{{ viewing.pending_tasks }}</div></div>
          <div v-if="viewing.equipment_status"><strong>Equipment</strong><div class="hms-pre">{{ viewing.equipment_status }}</div></div>
          <div v-if="viewing.meds_to_check"><strong>Meds to check</strong><div class="hms-pre">{{ viewing.meds_to_check }}</div></div>
          <div v-if="viewing.general_notes"><strong>Notes</strong><div class="hms-pre">{{ viewing.general_notes }}</div></div>
        </div>
        <div class="hms-modal-foot">
          <button v-if="viewing.status === 'submitted'" class="hms-btn hms-btn-success" @click="approve(viewing)"><i class="mdi mdi-check me-1"></i>Approve</button>
          <button class="hms-btn hms-btn-neutral" @click="viewing=null">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { executeJson, hms } from "@/api";

export default {
  name: "ShiftHandover",
  data() {
    return {
      tab: 'open', handovers: [], units: [],
      showForm: false, saving: false, viewing: null,
      form: this.emptyForm()
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.loadUnits();
    this.load();
  },
  methods: {
    emptyForm() {
      return {
        shift_type: 'morning', shift_date: new Date().toISOString().slice(0,10),
        unit_id: null, department: '', incoming_staff_id: null,
        critical_patients: '', pending_tasks: '', equipment_status: '', meds_to_check: '', general_notes: ''
      };
    },
    async loadUnits() {
      var r = await executeJson({ function: "listHospitalUnits" }, hms);
      if (r.data && r.data.success) this.units = r.data.data.units || [];
    },
    async load() {
      var params = { function: "getShiftHandovers" };
      if (this.tab === 'approved') params.status = 'approved';
      else if (this.tab === 'overdue') params.status = 'overdue';
      else if (this.tab === 'open') params.status = 'submitted';
      var r = await executeJson(params, hms);
      if (r.data && r.data.success) this.handovers = r.data.data.handovers || [];
    },
    openNew() { this.form = this.emptyForm(); this.showForm = true; },
    openView(h) { this.viewing = h; },
    async save() {
      this.saving = true;
      try {
        var r = await executeJson(Object.assign({ function: "saveShiftHandover" }, this.form), hms);
        if (r.data && r.data.success) { this.showForm = false; await this.load(); }
      } finally { this.saving = false; }
    },
    async approve(h) {
      await executeJson({ function: "approveShiftHandover", handover_id: h.handover_id }, hms);
      this.viewing = null;
      await this.load();
    },
    statusBadge(s) {
      return ({ open:'hms-badge-amber', submitted:'hms-badge-info', approved:'hms-badge-success', rejected:'hms-badge-danger', overdue:'hms-badge-danger' })[s] || 'hms-badge-neutral';
    }
  }
};
</script>

<style scoped>
.hms-pre { white-space: pre-wrap; background:#f8fafc; border:1px solid #e0e6ed; border-radius:6px; padding:10px; margin:8px 0 14px; font-size:10pt; }
.hms-text-green { color: #28a745; }
</style>
